import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth, homeFor } from "@/context/AuthContext";
import { formatApiError, api } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

export const AuthShell = ({ title, sub, children }) => (
  <div className="grid min-h-screen md:grid-cols-[1fr_1.1fr]">
    <div className="flex flex-col px-6 py-8 md:px-14">
      <Brand />
      <div className="fade-up mx-auto flex w-full max-w-sm flex-1 flex-col justify-center py-10">
        <h1 className="font-display text-4xl font-bold tracking-tight">{title}</h1>
        <p className="mt-2 text-muted-foreground">{sub}</p>
        <div className="mt-8">{children}</div>
      </div>
    </div>
    <div className="grain relative hidden overflow-hidden bg-black md:flex md:flex-col md:items-center md:justify-center md:p-12">
      <div className="neon-ring w-full max-w-[460px] rounded-full p-[3px]">
        <div className="rounded-full bg-black">
        <img src="/brand/logo-512.webp" alt="Scoopz Private Rides logo" className="aspect-square w-full rounded-full bg-black" />
        </div>
      </div>
      <p className="font-brush mt-10 text-center text-2xl">
        <span className="neon-text-pink">Same people.</span> <span className="neon-text-cyan">More places.</span>
      </p>
      <p className="mt-2 text-xs font-semibold uppercase tracking-[0.35em] text-white/60">San Marcos · Kyle · Austin · New Braunfels</p>
    </div>
  </div>
);

export const Field = ({ id, label, ...props }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} className="h-11 rounded-xl" {...props} />
  </div>
);

function ConfigBanner() {
  const { data } = useQuery({ queryKey: ["configStatus"], queryFn: () => api.get("/config/status").then((r) => r.data) });
  if (!data || data.owner_configured) return null;
  return (
    <p className="mb-4 rounded-xl border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-xs text-amber-100" data-testid="owner-config-error">
      Configuration error: SCOOPZ_ADMIN_EMAIL is not set on the server. Riders can use Scoopz, but Admin Mode is disabled until the owner email is configured.
    </p>
  );
}

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={homeFor(user)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(email, password, remember);
      navigate(homeFor(u));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Welcome back" sub="Log in to book or drive with Scoopz.">
      <ConfigBanner />
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field id="email" label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
        <Field id="password" label="Password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
        <div className="flex items-center justify-between text-sm">
          <label className="flex cursor-pointer items-center gap-2 text-muted-foreground">
            <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="login-remember-checkbox" /> Remember me
          </label>
          <Link to="/forgot-password" className="font-semibold text-primary hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
        </div>
        {error && <p className="rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="login-error">{error}</p>}
        <Button type="submit" disabled={busy} className="h-12 w-full rounded-full text-base font-semibold" data-testid="login-submit-button">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Log in"}
        </Button>
        <p className="text-center text-sm text-muted-foreground">
          New to Scoopz? <Link to="/register" className="font-semibold text-primary hover:underline" data-testid="go-to-register-link">Create an account</Link>
        </p>
      </form>
    </AuthShell>
  );
}
